import { ArrowRightIcon, MinusCircleIcon } from "@heroicons/react/20/solid";
import React, {
  Fragment,
  useEffect,
  useId,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import SEO from "./components/SEO";
import { Button, Card } from "./components/ui";
import { usePluginList } from "./lib/contexts/PluginListContextUtils";
import {
  siteLinks,
  sitePages,
  suggestPage,
  type PageLink,
} from "./lib/Utils/suggest-page";

/**
 * A server's dashboard path that no dashboard route matched
 * ("/dashboard/<id>/<rule>/<extra>" falls through to the catch-all, which has
 * no :guildId param). Captures the server id when there is one.
 */
const DASHBOARD_PATH = /^\/dashboard\/(?:(\d{17,20})(?:\/|$))?/;

/** Longest the suggestion waits for the integration catalog. */
const CATALOG_WAIT_MS = 2000;

/**
 * Where the docs site lives: its own build, served from this host in
 * production and from another in development. The .env files are not in the
 * repository, and this module is part of the entry bundle, so a build without
 * them falls back to production's path rather than failing to start.
 */
const DOCS_URL: string = import.meta.env.VITE_DOCS_URL ?? "/docs";
const LINKS = siteLinks(DOCS_URL);

/**
 * A link to one of the pages the 404 offers. The app's own pages are router
 * links; everything else is static HTML served outside the SPA, hence a plain
 * <a href>: a router <Link> to /discord-… or /integrations/… would render
 * this component again.
 */
function PageAnchor({
  link,
  ...props
}: { link: PageLink } & Omit<ComponentPropsWithoutRef<"a">, "href">) {
  return link.inApp ? (
    <Link to={link.href} {...props} />
  ) : (
    <a href={link.href} {...props} />
  );
}

/**
 * The address a link leads to, the way the address bar shows it, so the
 * visitor can see (and re-bookmark) the corrected URL. A line may break after
 * any "/": browsers break a URL only at "-" of their own accord, and a long
 * docs address would split mid-word on a phone.
 */
function Address({ href }: { href: string }) {
  const url = new URL(href, window.location.href);
  return (
    <>
      {`${url.host}${url.pathname}`.split("/").map((part, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <>
              /<wbr />
            </>
          )}
          {part}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Two different 404s share this component.
 *
 * Inside a server's dashboard (RuleEditor, RoleTest and ManagedRoles render it
 * when a rule or page is missing) the visitor is a signed-in manager, and the
 * way back is that server's dashboard.
 *
 * Everywhere else it is the catch-all for URLs that do not exist, and most of
 * those were never links on this site: AI assistants invent RoleLogic URLs
 * from the question they were asked, and old links still point at pages that
 * moved. So the page suggests the page the path most likely meant
 * (lib/Utils/suggest-page.ts) and offers the site's front doors — the
 * dashboard among them, where most visitors in GA4 went next.
 *
 * Either way the page stays noindex — an unknown URL must never be indexed.
 */
const PageNotFound: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { guildId } = useParams<{ guildId: string }>();
  const { t } = useTranslation();
  const { plugins, loading } = usePluginList();
  const linksLabelId = useId();

  const dashboardPath =
    guildId === undefined ? DASHBOARD_PATH.exec(pathname) : null;
  const inDashboard = guildId !== undefined || dashboardPath !== null;

  const handleGoBack = () => {
    navigate(`/dashboard/${guildId ?? dashboardPath?.[1] ?? ""}`);
  };

  // Integrations are candidates too, so wait for the catalog rather than
  // suggest without it (it lands ~0.3 s after mount on Slow 4G). But not for
  // ever: the fetch has no timeout, and a stalled one must not also withhold
  // a match that only needed the answer or docs pages. A catalog that failed
  // to load leaves those to match.
  const [stopWaiting, setStopWaiting] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setStopWaiting(true), CATALOG_WAIT_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  // Decided once per address, when the catalog is in or the wait is over,
  // and kept: a catalog that turns up after the wait must not swap or take
  // away a suggestion the visitor may already be reaching for. (State set
  // while rendering: React renders again at once, before anything is shown.)
  const [decided, setDecided] = useState<{
    path: string;
    page: PageLink | null;
  } | null>(null);
  if (!inDashboard && (!loading || stopWaiting) && decided?.path !== pathname) {
    setDecided({
      path: pathname,
      page: suggestPage(pathname, sitePages(plugins, DOCS_URL)),
    });
  }
  const suggestion = decided?.path === pathname ? decided.page : undefined;

  const popularLinks: PageLink[] = [
    { ...LINKS.home, label: t("PageNotFound.homeLink") },
    { ...LINKS.dashboard, label: t("PageNotFound.dashboardLink") },
    { ...LINKS.roleBotGuide, label: t("PageNotFound.roleBotGuideLink") },
    { ...LINKS.integrations, label: t("PageNotFound.integrationsLink") },
    { ...LINKS.docs, label: t("PageNotFound.docsLink") },
  ];
  // The front doors and the app's own pages are named in the visitor's
  // language, the app pages as its footer and menu name them; answer pages,
  // integrations and docs pages keep the English they are written in. A
  // suggested front door is not offered a second time underneath.
  const translatedLabels = new Map<string, string>([
    ...popularLinks.map(({ href, label }): [string, string] => [href, label]),
    ["/privacy", t("HomeLayout.privacy")],
    ["/terms", t("HomeLayout.terms")],
    ["/status", t("HomeLayout.status")],
    ["/upgrade", t("UserDropdown.upgradeLink")],
  ]);
  const suggested = suggestion && {
    ...suggestion,
    label: translatedLabels.get(suggestion.href) ?? suggestion.label,
  };
  const otherLinks = popularLinks.filter(
    (link) => link.href !== suggested?.href,
  );

  return (
    <div
      className={
        inDashboard
          ? "flex flex-1 items-center justify-center p-4"
          : // Top-aligned: the suggestion and the links arrive a moment after
            // the heading, and a centred card would move it as they did.
            "flex flex-1 items-start justify-center px-4 pb-4 pt-10 sm:pt-20"
      }
    >
      <SEO
        title="Page Not Found"
        description="The page you're looking for doesn't exist."
        noindex
      />
      <Card
        padding="md"
        className="w-full max-w-2xl shadow-2xl space-y-4 text-center"
      >
        <MinusCircleIcon
          className="mx-auto h-16 w-16 text-red-500"
          aria-hidden="true"
        />
        <h2 className="text-2xl font-bold text-white">
          {t("PageNotFound.pageNotFoundTitle")}
        </h2>
        <p className="text-gray-400">{t("PageNotFound.pageNotFoundMessage")}</p>
        {inDashboard ? (
          <Button onClick={handleGoBack} className="mt-4">
            {t("PageNotFound.goBackToDashboardButton")}
          </Button>
        ) : (
          // Held back until the suggestion is decided, then shown together,
          // below everything already on screen: nothing moves under a finger
          // that was reaching for a link.
          suggestion !== undefined && (
            <>
              {suggested && (
                // One link for the whole card: the question, the page and
                // where it lives are a single target, large enough to hit on
                // a phone.
                <PageAnchor
                  link={suggested}
                  className="group mx-auto mt-2 block max-w-md rounded-xl border border-indigo-500/40 bg-indigo-500/10 px-5 py-4 text-start transition-colors hover:border-indigo-400 hover:bg-indigo-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
                >
                  <span className="block text-sm text-indigo-200">
                    {t("PageNotFound.didYouMean")}
                  </span>
                  <span className="mt-1 flex items-center justify-between gap-3 text-lg font-semibold text-white">
                    {suggested.label}
                    <ArrowRightIcon
                      className="h-5 w-5 shrink-0 text-indigo-300 transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </span>
                  {/* Mid-word only if one segment cannot fit a line. */}
                  <span className="mt-0.5 block wrap-anywhere text-sm text-gray-400">
                    <Address href={suggested.href} />
                  </span>
                </PageAnchor>
              )}
              <nav aria-labelledby={linksLabelId} className="pt-2">
                <p
                  id={linksLabelId}
                  className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400"
                >
                  {t("PageNotFound.popularPages")}
                </p>
                <ul className="flex flex-wrap justify-center gap-2">
                  {otherLinks.map((link) => (
                    <li key={link.href}>
                      <Button asChild variant="ghost" size="sm">
                        <PageAnchor link={link}>{link.label}</PageAnchor>
                      </Button>
                    </li>
                  ))}
                </ul>
              </nav>
            </>
          )
        )}
      </Card>
    </div>
  );
};

export default PageNotFound;
